/**
 * Encryption module for API keys and secrets.
 *
 * AES-256-GCM (authenticated encryption).
 *
 * Env vars:
 * - ENCRYPTION_KEY — 64 hex chars (32 bytes)
 *
 * Storage format (TEXT columns):
 * - gcm:<iv_hex>:<authTag_hex>:<ciphertext_hex>
 */

import {
  createCipheriv,
  createDecipheriv,
  randomBytes,
} from "crypto"

// ==================== Lazy Key Loading ====================

let cachedKey: Buffer | null = null

function getKey(): Buffer {
  if (!cachedKey) {
    const key = process.env.ENCRYPTION_KEY
    if (!key) throw new Error("ENCRYPTION_KEY not configured")
    if (!/^[0-9a-fA-F]{64}$/.test(key)) {
      throw new Error("ENCRYPTION_KEY must be exactly 64 hex chars")
    }
    cachedKey = Buffer.from(key, "hex")
  }
  return cachedKey
}

// ==================== GCM Encrypt/Decrypt ====================

const GCM_ALGORITHM = "aes-256-gcm"
const GCM_IV_LENGTH = 12
const GCM_PREFIX = "gcm:"

function encryptGcm(text: string): string {
  const key = getKey()
  const iv = randomBytes(GCM_IV_LENGTH)
  const cipher = createCipheriv(GCM_ALGORITHM, key, iv)

  const encrypted = Buffer.concat([
    cipher.update(text, "utf8"),
    cipher.final(),
  ])
  const authTag = cipher.getAuthTag()

  return (
    GCM_PREFIX +
    iv.toString("hex") +
    ":" +
    authTag.toString("hex") +
    ":" +
    encrypted.toString("hex")
  )
}

function decryptGcm(data: string): string {
  const key = getKey()
  // Strip "gcm:" prefix
  const parts = data.slice(GCM_PREFIX.length).split(":")
  if (parts.length !== 3) {
    throw new Error("Invalid GCM encrypted data format")
  }

  const iv = Buffer.from(parts[0], "hex")
  const authTag = Buffer.from(parts[1], "hex")
  const ciphertext = Buffer.from(parts[2], "hex")

  const decipher = createDecipheriv(GCM_ALGORITHM, key, iv)
  decipher.setAuthTag(authTag)

  let decrypted = decipher.update(ciphertext, undefined, "utf8")
  decrypted += decipher.final("utf8")
  return decrypted
}

// ==================== Public API ====================

/**
 * Encrypts plaintext using AES-256-GCM.
 * Returns format: gcm:<iv>:<tag>:<ciphertext>
 */
export function encrypt(text: string): string {
  return encryptGcm(text)
}

/**
 * Decrypts AES-256-GCM encrypted data.
 * Expects format: gcm:<iv>:<tag>:<ciphertext>
 */
export function decrypt(encryptedText: string): string {
  if (encryptedText.startsWith(GCM_PREFIX)) {
    return decryptGcm(encryptedText)
  }
  throw new Error("Legacy CBC format no longer supported")
}

// ==================== Test Helpers ====================

/**
 * Clears cached keys. Use in tests only.
 * @internal
 */
export function _resetKeysForTesting(): void {
  cachedKey = null
}
